import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../../App";
import { formatDate } from "../../../../../hooks/Method";

const GeneralPrinterReceipt = ({ data }) => {
  const { settings } = useContext(AppContext);
  const [store, setStore] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    setStore({ ...data.store });
    return () => {};
  }, [data]);

  return data.id && store.id ? (
    <div
      style={{
        maxWidth: "210mm", // A4 width
        width: "100%",
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: "14px",
        lineHeight: 1.6,
        color: "#333",
        backgroundColor: "#fff",
        padding: "20px",
        margin: "0 auto", // Center the content
        boxSizing: "border-box",
      }}
    >
      {/* Store Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "20px",
          paddingBottom: "15px",
        }}
      >
        {store.logo && store.showLogoOnReceipt === true && (
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={store.logo}
              alt="store_logo"
              style={{
                maxHeight: "80px",
                maxWidth: "160px",
                objectFit: "contain",
              }}
            />
          </div>
        )}
        {store.showStoreNameOnReceipt && (
          <>
            <div
              style={{
                fontSize: "24px",
                fontWeight: "700",
                letterSpacing: "0.5px",
                color: "#222",
                marginBottom: "8px",
              }}
            >
              {store.storeName}
            </div>
            {store.localName && (
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                  color: "#444",
                  marginBottom: "6px",
                }}
              >
                {store.localName}
              </div>
            )}
          </>
        )}
        {store.showTaglineOnReceipt && (
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              letterSpacing: "0.5px",
              color: "#444",
              marginBottom: "6px",
            }}
          >
            {store.tagline}
          </div>
        )}
        {(store.showAddressOnReceipt || store.showPhoneOnReceipt) && (
          <div
            style={{
              fontSize: "14px",
              color: "#666",
              lineHeight: 1.4,
              marginTop: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {store.showAddressOnReceipt && (
              <>
                {store.address && (
                  <span
                    style={{
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {store.address}
                  </span>
                )}
                <div>
                  {store.city ? `, ${store.city}` : ""}
                  {store.state ? `, ${store.state}` : ""}
                  {store.zip ? `, ${store.zip}` : ""}
                </div>
              </>
            )}
            <div
              style={{ display: "flex", gap: "8px", justifyContent: "center" }}
            >
              {store.showPhoneOnReceipt && store.phone && (
                <span>📞 {store.phone}</span>
              )}
              {store.showEmailOnReceipt && store.email && (
                <span>✉️ {store.email}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Receipt Title */}
      <div
        style={{
          textAlign: "center",
          fontSize: "18px",
          fontWeight: "600",
          margin: "0 0 25px 0",
          color: "#333",
          textTransform: "uppercase",
          letterSpacing: "1px",
          paddingBottom: "3px",
          borderBottom: "2px solid #ddd",
        }}
      >
        {t("quoteSaleReceipt")}
      </div>

      {/* Transaction Details */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <div>
          <div style={{ fontWeight: "600", color: "#555" }}>{t("id")}</div>
          <div style={{ color: "#333" }}>#{data.id}</div>
        </div>
        <div>
          <div style={{ fontWeight: "600", color: "#555" }}>{t("date")}</div>
          <div style={{ color: "#333" }}>{formatDate(data.quoteDate)}</div>
        </div>
        <div>
          <div style={{ fontWeight: "600", color: "#555" }}>{t("status")}</div>
          <div style={{ color: "#333" }}>{data.statusName}</div>
        </div>
        {data.customer && (
          <>
            <div>
              <div style={{ fontWeight: "600", color: "#555" }}>
                {t("customer")}
              </div>
              <div style={{ color: "#333" }}>{data.customer.fullName}</div>
            </div>
            <div>
              <div style={{ fontWeight: "600", color: "#555" }}>
                {t("phone")}
              </div>
              <div style={{ color: "#333" }}>{data.customer.phone}</div>
            </div>
          </>
        )}
      </div>

      {/* Items Table */}
      <div
        style={{
          marginBottom: "20px",
          borderBottom: "2px solid #ddd",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2.5fr 0.50fr 1fr 1fr",
            gap: "12px",
            fontWeight: "600",
            marginBottom: "12px",
            color: "#555",
            paddingBottom: "8px",
            borderBottom: "1px solid #eee",
          }}
        >
          <div>{t("itemName")}</div>
          <div style={{ textAlign: "center" }}>{t("qty")}</div>
          <div style={{ textAlign: "right" }}>{t("unitPrice")}</div>
          <div style={{ textAlign: "right" }}>{t("totalPrice")}</div>
        </div>

        {data.quoteItems?.map((item, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "2.5fr 0.50fr 1fr 1fr",
              gap: "12px",
              marginBottom: "4px",
              color: "#333",
              paddingBottom: "4px",
              borderBottom: `${
                data.quoteItems.length > i + 1 ? "1" : "0"
              }px solid #eee`,
            }}
          >
            <div>{item.product.label}</div>
            <div style={{ textAlign: "center" }}>{item.qty}</div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol} {item.price.toFixed(settings.decimal)}
            </div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol}{" "}
              {(item.qty * item.price).toFixed(settings.decimal)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals Section */}
      <div
        style={{
          display: "grid",
          gap: "6px",
          marginBottom: "15px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#555" }}>{t("subtotal")}:</span>
          <span style={{ color: "#333" }}>
            {settings.curSymbol} {data.subtotal.toFixed(settings.decimal)}
          </span>
        </div>

        {data.tax > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#555" }}>
              {t("tax")} ({data.taxPct}%):
            </span>
            <span style={{ color: "#333" }}>
              +{settings.curSymbol} {data.tax.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        {data.disAmt > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#555" }}>{t("discount")}:</span>
            <span style={{ color: "#333" }}>
              -{settings.curSymbol} {data.disAmt.toFixed(settings.decimal)}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "600",
            paddingTop: "12px",
            borderTop: "1px solid #ddd",
            color: "#222",
          }}
        >
          <span>{t("toPay")}:</span>
          <span>
            {settings.curSymbol} {data.toPay.toFixed(settings.decimal)}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "grid",
          fontSize: "12px",
          color: "#666",
          lineHeight: 1.4,
          borderTop: "2px solid #ddd",
          paddingTop: "15px",
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
          maxWidth: "100%",
        }}
      >
        {data.note && (
          <span
            style={{
              borderBottom: "1px solid #ddd",
              paddingBottom: "10px",
              marginBottom: "10px",
            }}
          >
            {data.note}
          </span>
        )}
        <span>{settings.salesTermForReceipt}</span>
      </div>
    </div>
  ) : null;
};

export default GeneralPrinterReceipt;
